import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngrx/store';
import {
  getUser,
  getUserSuccess,
  loadUsers
} from './features/user/user.actions';
import { selectUser } from './features/user/user.selectors';
import { combineLatest, first, take } from 'rxjs';
import { User } from './models/user';
import {
  loadOrganizations,
  loadOrganizationsFailure,
  loadOrganizationsSuccess,
  selectOrganization
} from './features/organization/organization.actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  selectAllOrganizations,
  selectCurrentOrganization,
  selectCurrentOrganizationId,
} from './features/organization/organization.selectors';
import { Title } from '@angular/platform-browser';
import { Organization } from './models/organization';
import { loadSectors } from './features/sector/sector.actions';
import { Actions, ofType } from '@ngrx/effects';
import {
  activateAdvancedFeaturesFailure,
  activateAdvancedFeaturesSuccess,
  deactivateAdvancedFeaturesFailure,
  deactivateAdvancedFeaturesSuccess,
  loadSites,
  loadSitesFailure,
  loadSitesSuccess,
} from './features/site/site.actions';
import { MatDialog } from '@angular/material/dialog';
import {
  UserConfigurationErrorDialog
} from './shared/dialogs/user-configuration-error-dialog/user-configuration-error.dialog';
import {
  selectConfigDisplaySitesFilters,
  selectConfigLeftPanelMode,
  selectConfigRightPanelMode
} from './features/config/config.selectors';
import { ConfigPanelMode } from './enums/config';
import { setConfigLeftPanelMode } from './features/config/config.actions';
import { TranslocoService } from '@jsverse/transloco';
import {
  startPerreadPipelineFailure,
  startPerreadPipelineSuccess
} from './features/pipeline/pipeline.actions';
import { AppInitService } from './init/app-init.service';
import { WsService } from './services/ws.service';
import { selectSiteIds } from './features/site/site.selectors';
import Keycloak from 'keycloak-js';
import {
  loadDetectionLabels
} from './features/detection-label/detection-label.actions';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {

  private organization: Organization | undefined | null;

  leftSideMode: ConfigPanelMode = ConfigPanelMode.MODE_EXPANDED;
  rightSideMode: ConfigPanelMode = ConfigPanelMode.MODE_HIDDEN;
  sideModeEnum = ConfigPanelMode;
  displaySitesFilters = false;

  constructor(
    private store: Store,
    private keycloak: Keycloak,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private actions: Actions,
    public title: Title,
    private translocoService: TranslocoService,
    private appInitService: AppInitService,
    private wsService: WsService
  ) {
    this.translocoService.setActiveLang(
      navigator.language.slice(0, 2)
    );

    this.handleConfig();
    this.handleOrganization();
    this.handleUser();
    this.handleAdvancedFeatures();
    this.handlePipeline();
    this.handleSites();

    this.store.dispatch(getUser());
  }

  ngOnInit(): void {
    this.actions
      .pipe(
        ofType(getUserSuccess),
        first()
      )
      .subscribe(result => {
        const user = result['user'];

        this.translocoService.setActiveLang(
          user.languageCode
        );

        this.store.dispatch(loadDetectionLabels());

        void this.appInitService.initAfterAuth();
      });
  }

  private handlePipeline(): void {
    this.actions
      .pipe(
        ofType(startPerreadPipelineSuccess),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.store
          .select(selectUser)
          .pipe(first())
          .subscribe((user: User | undefined) => {
            if (user) {
              this.toastr.success(
                this.translocoService.translate(
                  'start_perread_pipeline_success',
                  { mail: user.email }
                )
              );
            }
          });
      });

    this.actions
      .pipe(
        ofType(startPerreadPipelineFailure),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.toastr.error(
          this.translocoService.translate(
            'start_perread_pipeline_failure'
          )
        );
      });
  }

  private handleConfig(): void {
    this.store
      .select(selectConfigLeftPanelMode)
      .pipe(takeUntilDestroyed())
      .subscribe((sideMode: ConfigPanelMode) => {
        this.leftSideMode = sideMode;
      });

    this.store
      .select(selectConfigRightPanelMode)
      .pipe(takeUntilDestroyed())
      .subscribe((sideMode: ConfigPanelMode) => {
        this.rightSideMode = sideMode;
      });

    this.store
      .select(selectConfigDisplaySitesFilters)
      .pipe(takeUntilDestroyed())
      .subscribe((displaySitesFilters: boolean) => {
        this.displaySitesFilters = displaySitesFilters;
      });
  }

  private handleUser(): void {
    this.store
      .select(selectUser)
      .pipe(takeUntilDestroyed())
      .subscribe(result => {
        if (result) {
          this.store.dispatch(loadOrganizations());
        }
      });
  }

  private handleOrganization(): void {
    this.store
      .select(selectCurrentOrganization)
      .pipe(takeUntilDestroyed())
      .subscribe(
        (
          organization:
            | Organization
            | null
            | undefined
        ) => {
          if (!organization) {
            return;
          }

          if (
            !this.organization ||
            this.organization.id !== organization.id
          ) {
            this.organization = organization;

            this.title.setTitle(
              this.organization.name
            );

            this.store.dispatch(
              loadSectors({
                organizationId:
                this.organization.id
              })
            );

            this.store.dispatch(
              loadSites({
                organizationId:
                this.organization.id
              })
            );

            this.store.dispatch(
              loadUsers({
                organizationId:
                this.organization.id
              })
            );
          }
        }
      );

    this.actions
      .pipe(
        ofType(loadOrganizationsSuccess),
        first()
      )
      .subscribe(result => {
        const organizations =
          result['organizations'];

        if (organizations.length === 0) {
          this.displayUserConfigurationErrorDialog();
        }
      });

    this.actions
      .pipe(
        ofType(loadOrganizationsFailure),
        first()
      )
      .subscribe(() => {
        this.displayUserConfigurationErrorDialog();
      });

    this.store
      .select(selectAllOrganizations)
      .pipe(takeUntilDestroyed())
      .subscribe(result => {
        if (
          result.length &&
          !this.organization
        ) {
          const organization = result[0];

          this.store.dispatch(
            selectOrganization({
              organizationId:
              organization.id
            })
          );
        }
      });
  }

  private handleAdvancedFeatures(): void {
    this.actions
      .pipe(
        ofType(
          activateAdvancedFeaturesSuccess
        ),
        takeUntilDestroyed()
      )
      .subscribe(data => {
        const site: any =
          data['update']['changes'];

        this.toastr.success(
          this.translocoService.translate(
            'advanced_features_activating',
            { site: site['name'] }
          )
        );
      });

    this.actions
      .pipe(
        ofType(
          activateAdvancedFeaturesFailure
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.toastr.error(
          this.translocoService.translate(
            'activate_advanced_features_failure'
          )
        );
      });

    this.actions
      .pipe(
        ofType(
          deactivateAdvancedFeaturesSuccess
        ),
        takeUntilDestroyed()
      )
      .subscribe(data => {
        const site: any =
          data['update']['changes'];

        this.toastr.success(
          this.translocoService.translate(
            'deactivate_advanced_features_success',
            { site: site['name'] }
          )
        );
      });

    this.actions
      .pipe(
        ofType(
          deactivateAdvancedFeaturesFailure
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.toastr.error(
          this.translocoService.translate(
            'deactivate_advanced_features_failure'
          )
        );
      });
  }

  private handleSites(): void {
    this.actions
      .pipe(
        ofType(loadSitesSuccess),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.wsService.unsubscribeAll();

        combineLatest([
          this.store.select(
            selectCurrentOrganizationId
          ),
          this.store.select(selectSiteIds)
        ])
          .pipe(take(1))
          .subscribe(
            ([organizationId, siteIds]) => {
              if (
                !organizationId ||
                !siteIds
              ) {
                return;
              }

              for (const siteId of siteIds) {
                this.wsService.subscribeTopic({
                  organization:
                  organizationId,
                  site: siteId
                });
              }
            }
          );
      });

    this.actions
      .pipe(
        ofType(loadSitesFailure),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.wsService.unsubscribeAll();
      });
  }

  private displayUserConfigurationErrorDialog(): void {
    const dialogRef =
      this.dialog.open(
        UserConfigurationErrorDialog,
        {
          disableClose: false
        }
      );

    dialogRef
      .afterClosed()
      .subscribe(() => {
        /*
         * On ferme d'abord volontairement
         * la WebSocket pour empêcher toute
         * tentative de reconnexion pendant
         * le logout.
         */
        this.wsService.close();

        this.keycloak
          .logout()
          .catch(err =>
            this.toastr.error(err)
          );
      });
  }

  toggleLeftSideMode(): void {
    const mode =
      this.leftSideMode ===
      this.sideModeEnum.MODE_EXPANDED
        ? this.sideModeEnum.MODE_COLLAPSED
        : this.sideModeEnum.MODE_EXPANDED;

    this.store.dispatch(
      setConfigLeftPanelMode({ mode })
    );
  }
}
